import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import {
  IReferral,
  ReferralStatus,
} from '@principle-theorem/principle-core/interfaces';
import { WithRef } from '@principle-theorem/shared';

interface IStatusTransition {
  targetStatus: ReferralStatus;
  label: string;
  icon: string;
  iconClass: string;
}

@Component({
  selector: 'pr-referral-menu-status-buttons',
  imports: [MatIconModule, MatButtonModule, MatMenuModule],
  templateUrl: './referral-menu-status-buttons.component.html',
  styleUrl: './referral-menu-status-buttons.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralMenuStatusButtonsComponent {
  private readonly _baseTransitions = {
    draft: {
      targetStatus: ReferralStatus.Draft,
      icon: 'edit',
      iconClass: '!text-slate-300',
    },
    sent: {
      targetStatus: ReferralStatus.Sent,
      icon: 'send',
      iconClass: '!text-primary-500',
    },
    received: {
      targetStatus: ReferralStatus.Received,
      icon: 'inbox',
      iconClass: '!text-yellow-500',
    },
    completed: {
      targetStatus: ReferralStatus.Completed,
      icon: 'check_circle',
      iconClass: '!text-green-500',
    },
  };

  private readonly _statusTransitions: Record<
    ReferralStatus,
    IStatusTransition[]
  > = {
    [ReferralStatus.Draft]: [
      { ...this._baseTransitions.sent, label: 'Mark as Sent' },
      { ...this._baseTransitions.received, label: 'Mark as Received' },
      { ...this._baseTransitions.completed, label: 'Mark as Complete' },
    ],
    [ReferralStatus.Sent]: [
      { ...this._baseTransitions.draft, label: 'Revert to Draft' },
      { ...this._baseTransitions.received, label: 'Mark as Received' },
      { ...this._baseTransitions.completed, label: 'Mark as Complete' },
    ],
    [ReferralStatus.Received]: [
      { ...this._baseTransitions.draft, label: 'Revert to Draft' },
      { ...this._baseTransitions.sent, label: 'Mark as Sent' },
      { ...this._baseTransitions.completed, label: 'Mark as Complete' },
    ],
    [ReferralStatus.Completed]: [
      { ...this._baseTransitions.draft, label: 'Revert to Draft' },
      { ...this._baseTransitions.received, label: 'Mark as Received' },
      { ...this._baseTransitions.sent, label: 'Mark as Sent' },
    ],
  };

  referral = input.required<WithRef<IReferral>>();
  updateStatus = output<ReferralStatus>();

  availableTransitions = computed(() => {
    const currentReferral = this.referral();
    if (!currentReferral) {
      return [];
    }

    const transitions = this._statusTransitions[currentReferral.status];

    return currentReferral.emailInvitationSent
      ? transitions.filter((cur) => cur.targetStatus !== ReferralStatus.Draft)
      : transitions;
  });
}
